
import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EnhancedAuthOptions {
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  sessionTimeout?: number;
}

interface AdminUser {
  id: string;
  role: 'admin' | 'super_admin';
  is_active: boolean;
}

export const useEnhancedAuth = (options: EnhancedAuthOptions = {}) => {
  const { user, isAdmin, signOut } = useAuth();
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isSecure, setIsSecure] = useState(false);
  const [securityCheck, setSecurityCheck] = useState<'pending' | 'passed' | 'failed'>('pending');
  const [sessionExpiry, setSessionExpiry] = useState<Date | null>(null);
  const lastCheckRef = useRef<number>(0);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const {
    requireAdmin = false,
    requireSuperAdmin = false,
    sessionTimeout = 60
  } = options;

  const logSecurityEvent = useCallback(async (eventType: string, details?: any) => {
    try {
      await supabase.functions.invoke('log-security-event', {
        body: {
          userId: user?.id,
          eventType,
          eventDetails: details,
          userAgent: navigator.userAgent
        }
      });
    } catch (error) {
      // Silent error handling
    }
  }, [user?.id]);

  const performSecurityCheck = useCallback(async () => {
    const now = Date.now();
    
    if (now - lastCheckRef.current < 30000) {
      return;
    }
    
    lastCheckRef.current = now;

    try {
      if (!user) {
        setSecurityCheck('failed');
        setIsSecure(false);
        setAdminUser(null);
        return;
      }

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session || session.user.id !== user.id) {
        await logSecurityEvent('SESSION_INVALID');
        setSecurityCheck('failed');
        setIsSecure(false);
        setAdminUser(null);
        await signOut();
        return;
      }

      if (session.expires_at) {
        setSessionExpiry(new Date(session.expires_at * 1000));
      }

      if (requireAdmin || requireSuperAdmin) {
        if (!isAdmin) {
          await logSecurityEvent('ADMIN_ACCESS_DENIED', { reason: 'User is not admin' });
          setSecurityCheck('failed');
          setIsSecure(false);
          setAdminUser(null);
          return;
        }

        const { data: adminData, error: detailError } = await supabase
          .from('admin_users')
          .select('id, role, is_active')
          .eq('user_id', user.id)
          .maybeSingle();

        if (detailError) {
          await logSecurityEvent('ADMIN_DETAIL_FETCH_FAILED', { error: detailError });
        }

        if (adminData) {
          if (adminData.role !== 'admin' && adminData.role !== 'super_admin') {
            await logSecurityEvent('INVALID_ADMIN_ROLE', { role: adminData.role });
            setSecurityCheck('failed');
            setIsSecure(false);
            setAdminUser(null);
            await signOut();
            return;
          }

          if (requireSuperAdmin && adminData.role !== 'super_admin') {
            await logSecurityEvent('SUPER_ADMIN_ACCESS_DENIED', { userRole: adminData.role });
            setSecurityCheck('failed');
            setIsSecure(false);
            setAdminUser(null);
            toast.error('Super admin access required');
            return;
          }

          const typedAdminUser: AdminUser = {
            id: adminData.id,
            role: adminData.role as 'admin' | 'super_admin',
            is_active: adminData.is_active
          };

          setAdminUser(typedAdminUser);
          await logSecurityEvent('ADMIN_ACCESS_GRANTED', { role: adminData.role });
        } else {
          setAdminUser({
            id: user.id,
            role: 'admin',
            is_active: true
          });
        }
      }

      setSecurityCheck('passed');
      setIsSecure(true);

    } catch (error) {
      await logSecurityEvent('SECURITY_CHECK_ERROR', { error: error.message });
      setSecurityCheck('failed');
      setIsSecure(false);
      setAdminUser(null);
      await signOut();
    }
  }, [user, requireAdmin, requireSuperAdmin, isAdmin, logSecurityEvent, signOut]);

  useEffect(() => {
    if (!sessionExpiry) return;

    const checkSessionExpiry = () => {
      const now = new Date();
      const timeUntilExpiry = sessionExpiry.getTime() - now.getTime();
      const minutesUntilExpiry = Math.floor(timeUntilExpiry / (1000 * 60));

      if (minutesUntilExpiry === 5) {
        toast.warning('Your session will expire in 5 minutes. Please save your work.');
      }

      if (minutesUntilExpiry === 1) {
        toast.error('Your session will expire in 1 minute!');
      }

      if (timeUntilExpiry <= 0) {
        toast.error('Session expired. Please log in again.');
        signOut();
      }
    };

    const interval = setInterval(checkSessionExpiry, 60000);
    return () => clearInterval(interval);
  }, [sessionExpiry, signOut]);

  useEffect(() => {
    performSecurityCheck();
    
    checkIntervalRef.current = setInterval(performSecurityCheck, 5 * 60 * 1000);
    
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [performSecurityCheck]);

  const isSuperAdmin = adminUser?.role === 'super_admin';
  const isActiveAdmin = adminUser?.is_active === true;

  return {
    isSecure,
    securityCheck,
    adminUser,
    isSuperAdmin,
    isActiveAdmin,
    sessionExpiry,
    performSecurityCheck,
    logSecurityEvent
  };
};
